import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  BarChart2, Package, TrendingUp, Zap, Crown, Rocket,
  CheckCircle2, ArrowRight, Store, Brain, ChevronDown, ChevronUp,
  IndianRupee, Phone, Mail, MessageSquare, LineChart, ShieldCheck,
  ShoppingCart, FileText, Truck, Users, Receipt, Bell,
  Menu, X, Wallet, RefreshCw, Building, Loader2,
} from 'lucide-react';

// ── Data ─────────────────────────────────────────────────────────────────────

const STATS = [
  { icon: Store, value: 'xx+', label: 'Stores on KiranaFlow' },
  { icon: IndianRupee, value: 'xx L+', label: 'Savings identified' },
  { icon: Package, value: 'xx K+', label: 'SKUs tracked live' },
  { icon: Receipt, value: 'xx K+', label: 'Bills generated' },
];

const ONE_CLICK = [
  {
    icon: MessageSquare,
    iconBg: 'bg-green-500',
    title: 'Bill on WhatsApp',
    desc: 'Customer gets a digital receipt on WhatsApp instantly after checkout. Zero paper, zero waiting — one tap.',
  },
  {
    icon: FileText,
    iconBg: 'bg-blue-500',
    title: 'GST Report to CA',
    desc: 'GSTR-1 and P&L are auto-calculated from your sales. Send them to your CA via WhatsApp or email in one tap.',
  },
  {
    icon: Truck,
    iconBg: 'bg-violet-500',
    title: 'Reorder to Supplier',
    desc: 'Running low? One tap sends a WhatsApp reorder to your distributor with item names and quantities pre-filled.',
  },
  {
    icon: Users,
    iconBg: 'bg-amber-500',
    title: 'Offers to Customers',
    desc: 'Send sale alerts, festival offers, and loyalty messages to your regular customers via WhatsApp in one tap.',
  },
];

const EXTRA_FEATURES = [
  { icon: Brain,       color: 'bg-violet-100 text-violet-700', title: 'AI Insights',         desc: 'Natural-language summaries of store performance — what\'s working, what\'s not, and what to do next.' },
  { icon: Zap,         color: 'bg-amber-100 text-amber-700',   title: 'Smart Actions',        desc: 'AI-ranked action cards for reorder, dead stock, expiry risk, and margin leakage — each showing rupee impact.' },
  { icon: LineChart,   color: 'bg-cyan-100 text-cyan-700',     title: 'Customer Insights',    desc: 'Top spenders, khata balances, purchase frequency, and loyalty trends across your customer base.' },
  { icon: ShieldCheck, color: 'bg-indigo-100 text-indigo-700', title: 'Role-Based Access',    desc: 'Owner, manager, and employee roles — each with scoped permissions so data stays in the right hands.' },
  { icon: Wallet,      color: 'bg-emerald-100 text-emerald-700', title: 'Savings Dashboard', desc: 'See the total rupee value locked in dead stock, expiry risk, and margin leakage — and track how much you\'ve recovered.' },
  { icon: Building,    color: 'bg-slate-100 text-slate-700',   title: 'Supplier Directory',   desc: 'Maintain supplier contacts, track purchase history per vendor, and send reorders directly from the app.' },
  { icon: Bell,        color: 'bg-orange-100 text-orange-700', title: 'Proactive Alerts',     desc: 'Expiry alerts 15 days ahead, dead stock flags at 30 days — before they silently hurt your revenue.' },
  { icon: RefreshCw,   color: 'bg-rose-100 text-rose-700',     title: 'Offline Billing',      desc: 'The POS works fully offline. Bills are queued locally and synced the moment connectivity returns.' },
];

const TIERS = [
  {
    key: 'starter', icon: Zap, label: 'Starter',
    badge: 'Intro Offer', badgeBg: 'bg-amber-100 text-amber-800',
    desc: 'Core tools to run your store digitally.',
    color: 'border-blue-200', headerBg: 'bg-blue-50', headerText: 'text-gray-900',
    dotColor: 'text-blue-500',
    pricing: [
      { label: '1 Year',  regular: '₹9,000',  intro: '₹6,000'  },
      { label: '2 Years', regular: '₹15,000', intro: '₹11,000' },
      { label: '3 Years', regular: '₹22,000', intro: '₹15,000' },
    ],
    features: [
      'POS Billing & Barcode Scanner',
      'Live Inventory & Stock Management',
      'Purchase Recording & Supplier Directory',
      'Credit / Khata Customer Accounts',
      'Sales History & Thermal Receipt Printing',
      'Product Catalog with Categories & Units',
    ],
  },
  {
    key: 'growth', icon: Rocket, label: 'Growth',
    badge: 'Free Trial', badgeBg: 'bg-white/20 text-white border border-white/40',
    desc: 'Intelligence & automation to grow your business.', popular: true,
    color: 'border-emerald-400', headerBg: 'bg-emerald-600', headerText: 'text-white',
    dotColor: 'text-emerald-500',
    trialColor: '#087860', trialLabelColor: '#065f46',
    trialNote: 'Live & evolving with early adopters. Pricing announced soon.',
    features: [
      'Everything in Starter, plus:',
      'Live Dashboard — KPIs & Revenue Charts',
      'AI Smart Actions with ₹ Rupee Impact Estimates',
      'Savings Tracker — Dead Stock & Expiry Alerts',
      'Customer Insights & Purchase History',
      'WhatsApp Bill Sharing after Checkout',
      'AI Invoice Scanning — Photo → Auto-fill',
    ],
  },
  {
    key: 'enterprise', icon: Crown, label: 'Enterprise',
    badge: 'Evolving', badgeBg: 'bg-purple-100 text-purple-700',
    desc: 'Full accounting & compliance for established stores.',
    color: 'border-purple-200', headerBg: 'bg-purple-50', headerText: 'text-gray-900',
    dotColor: 'text-purple-500',
    trialColor: '#7c3aed', trialLabelColor: '#5b21b6',
    trialNote: 'Built alongside enterprise partners. Pricing announced soon.',
    features: [
      'Everything in Growth, plus:',
      'Advanced Sales Reports & Category Analytics',
      'Seasonality & Trends with Festival Calendar',
      'P&L Statement with COGS from Purchase Bills',
      'GST Returns — GSTR-1 & GSTR-3B View',
      'Day Book, Sales & Purchase Registers',
      'Balance Sheet & Journal Ledger',
    ],
  },
];

const FAQS = [
  {
    q: 'Does KiranaFlow replace my billing software?',
    a: 'Yes — KiranaFlow is a complete ERP. It handles POS billing, inventory, purchases, accounting, and reporting. You don\'t need a separate billing or ERP tool. It also works offline, so billing never stops even without internet.',
  },
  {
    q: 'How does the WhatsApp bill sharing work?',
    a: 'After checkout, tap the WhatsApp icon on the bill screen. The customer\'s number is auto-filled from their profile (or you enter it once). A formatted digital receipt is sent to their WhatsApp in seconds — no printer needed.',
  },
  {
    q: 'Can I send my GST report directly to my CA?',
    a: 'Yes. Under Accounting → GST Returns, your GSTR-1 data is auto-generated from your recorded sales. Tap Share to send a formatted summary to your CA\'s WhatsApp or email in one tap — every month.',
  },
  {
    q: 'How does AI invoice scanning work?',
    a: 'On the Record Purchase screen, tap "Scan Invoice". Take a photo of your supplier\'s bill — KiranaFlow\'s AI reads the supplier name, date, bill number, and every line item. You review and confirm. Works with printed, handwritten, and PDF bills.',
  },
  {
    q: 'Does it work when the internet is down?',
    a: 'Yes. The POS works fully offline using local device storage. Bills are queued locally and synced to the server automatically when your connection returns. Sales history and receipt lookup also work offline.',
  },
  {
    q: 'What is a Smart Action?',
    a: 'A Smart Action is a prioritised recommendation with a specific rupee impact — for example, "Reorder Tata Salt: 3 days of stock left, ₹1,200 in lost sales risk" or "Amul Butter — selling below cost, ₹4,500 projected loss this month." You can address, dismiss, or track each one.',
  },
  {
    q: 'How is my store\'s data kept private?',
    a: 'Every store\'s data lives in a completely isolated database — no cross-store access is possible at any layer. Role-based access controls ensure employees only see what they\'re permitted to.',
  },
  {
    q: 'How do I get started?',
    a: 'Reach out to any of our team members below. We personally onboard every store — we\'ll set up your account, upload your catalog, and get your store live within 24 hours, with guidance throughout.',
  },
];

const TEAM = [
  {
    name: 'Rohan Kakad',
    role: 'Co-Founder',
    phone: '8888855212',
    displayPhone: '+91 88888 55212',
    email: 'rohankakad712@gmail.com',
    initials: 'RK',
    color: 'bg-emerald-600',
    shadow: 'shadow-emerald-200',
  },
  {
    name: 'Rupesh Shewale',
    role: 'Co-Founder',
    phone: '8237598340',
    displayPhone: '+91 82375 98340',
    email: 'rupeshshewale@gmail.com',
    initials: 'RS',
    color: 'bg-blue-600',
    shadow: 'shadow-blue-200',
  },
  {
    name: 'Shlok Deore',
    role: 'Co-Founder',
    phone: '9545991285',
    displayPhone: '+91 95459 91285',
    email: 'shlokdeore@gmail.com',
    initials: 'SD',
    color: 'bg-violet-600',
    shadow: 'shadow-violet-200',
  },
];

const WA_MSG = encodeURIComponent("Hi, I'm interested in KiranaFlow ERP for my store.");

const scrollToContact = () => {
  document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
};

// ── Mini mockup cards for each module ────────────────────────────────────────

function PosMockup() {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-xl overflow-hidden text-left w-full max-w-sm mx-auto">
      <div className="bg-emerald-600 px-4 py-3 flex items-center justify-between">
        <span className="text-white font-semibold text-sm">POS Billing</span>
        <div className="flex gap-1">
          {['C1', 'C2', 'C3'].map((c, i) => (
            <div key={c} className={`text-[9px] px-2 py-0.5 rounded font-bold ${i === 0 ? 'bg-white text-emerald-700' : 'bg-emerald-500 text-white'}`}>{c}</div>
          ))}
        </div>
      </div>
      <div className="p-4 space-y-1.5">
        {[['Tata Salt 1kg', '×2', '₹44'], ['Amul Butter 500g', '×1', '₹270'], ['Maggi 70g ×4', '×3', '₹222']].map(([n, q, t]) => (
          <div key={n} className="flex items-center justify-between text-xs py-1.5 border-b border-gray-50">
            <span className="text-gray-700 font-medium">{n}</span>
            <div className="flex items-center gap-3">
              <span className="text-gray-400">{q}</span>
              <span className="text-gray-800 font-bold w-10 text-right">{t}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="px-4 py-3 bg-gray-50 flex items-center justify-between">
        <div>
          <div className="text-[10px] text-gray-500">Total</div>
          <div className="text-xl font-extrabold text-gray-900">₹536</div>
        </div>
        <div className="flex items-center gap-1.5 bg-green-500 text-white text-xs font-bold px-4 py-2 rounded-xl shadow">
          <MessageSquare className="h-3.5 w-3.5" /> WhatsApp Bill
        </div>
      </div>
    </div>
  );
}

function InventoryMockup() {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-xl overflow-hidden text-left w-full max-w-sm mx-auto">
      <div className="bg-blue-600 px-4 py-3">
        <span className="text-white font-semibold text-sm">Stock Intelligence</span>
      </div>
      <div className="p-4 space-y-2">
        {[
          ['Tata Salt 1kg',   'Low Stock',    'bg-amber-100 text-amber-700',  '2 units left'],
          ['Maggi 70g',       'Expiry in 3d', 'bg-red-100 text-red-700',     '₹4,200 at risk'],
          ['Old Soap Bar',    'Dead 45 days', 'bg-gray-100 text-gray-500',   '₹1,800'],
          ['Amul Butter',     'Margin −5%',   'bg-orange-100 text-orange-700','Below cost'],
        ].map(([name, alert, cls, value]) => (
          <div key={name} className="flex items-center justify-between text-xs py-1">
            <span className="text-gray-700 font-medium">{name}</span>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${cls}`}>{alert}</span>
              <span className="text-gray-400 text-[10px]">{value}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="px-4 py-3 bg-blue-50 text-xs font-semibold text-blue-700 flex items-center gap-2">
        <Zap className="h-3.5 w-3.5 text-amber-500" />
        4 Smart Actions ready — ₹7,800 at risk
      </div>
    </div>
  );
}

function PurchaseMockup() {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-xl overflow-hidden text-left w-full max-w-sm mx-auto">
      <div className="bg-violet-600 px-4 py-3">
        <span className="text-white font-semibold text-sm">AI Invoice Scan</span>
      </div>
      <div className="p-4 space-y-3">
        <div className="bg-violet-50 rounded-xl p-3 text-center border border-violet-100">
          <div className="text-2xl mb-1">📄</div>
          <div className="text-xs font-semibold text-violet-700">Invoice scanned in 4 sec</div>
          <div className="text-[10px] text-violet-400 mt-0.5">Reliance Retail · 14 items · ₹8,240</div>
        </div>
        {[['Tata Salt 1kg', '24 units', '₹19/unit'], ['Fortune Oil 1L', '12 units', '₹138/unit'], ['Surf Excel 1kg', '6 units', '₹212/unit']].map(([n, q, p]) => (
          <div key={n} className="flex items-center justify-between text-xs border-b border-gray-50 pb-1.5">
            <span className="text-gray-700 font-medium">{n}</span>
            <span className="text-gray-400">{q} · {p}</span>
          </div>
        ))}
      </div>
      <div className="px-4 py-3 bg-gray-50 flex items-center justify-between">
        <span className="text-[10px] text-gray-400">+11 more items parsed</span>
        <div className="bg-violet-600 text-white text-xs font-bold px-4 py-2 rounded-xl">Confirm & Save</div>
      </div>
    </div>
  );
}

function ReportsMockup() {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-xl overflow-hidden text-left w-full max-w-sm mx-auto">
      <div className="bg-amber-600 px-4 py-3">
        <span className="text-white font-semibold text-sm">Accounting — May 2026</span>
      </div>
      <div className="p-4 space-y-2.5">
        {[
          ['Total Sales',        '₹1,24,500', 'text-gray-900 font-semibold'],
          ['Total Purchases',    '₹87,200',   'text-gray-700'],
          ['GST Collected',      '₹11,205',   'text-emerald-700 font-semibold'],
          ['GST Paid (Input)',   '₹7,848',    'text-blue-700'],
          ['Net Profit (P&L)',   '₹29,252',   'text-emerald-700 font-bold'],
        ].map(([label, value, cls]) => (
          <div key={label} className="flex items-center justify-between text-xs border-b border-gray-50 pb-2">
            <span className="text-gray-500">{label}</span>
            <span className={cls}>{value}</span>
          </div>
        ))}
      </div>
      <div className="px-4 py-3 bg-amber-50 flex items-center gap-2 text-xs font-bold text-green-700">
        <MessageSquare className="h-3.5 w-3.5" />
        Share to CA on WhatsApp →
      </div>
    </div>
  );
}

const MODULES = [
  {
    id: 'pos',
    badge: 'Billing & POS',
    badgeColor: 'bg-emerald-100 text-emerald-700',
    headline: 'Lightning-fast billing with barcode scanner',
    desc: 'Bill customers in seconds, manage multiple customer tabs simultaneously, and send digital receipts to WhatsApp instantly after checkout. Works offline — billing never stops.',
    features: [
      'Barcode scanner with instant product lookup',
      'Multiple concurrent customer carts (C1, C2, C3...)',
      'Offline billing — works without internet',
      'WhatsApp bill to customer in one tap after checkout',
    ],
    highlights: [{ label: 'WhatsApp Bills', color: 'bg-green-100 text-green-700' }, { label: 'Offline Ready', color: 'bg-blue-100 text-blue-700' }],
    Mockup: PosMockup,
  },
  {
    id: 'inventory',
    badge: 'Stock & Inventory',
    badgeColor: 'bg-blue-100 text-blue-700',
    headline: 'Real-time stock that updates on every sale',
    desc: 'Every purchase and sale updates your stock instantly. Get expiry alerts 15 days ahead, dead stock flags at 30 days, and smart reorder scores per SKU — all prioritised by rupee impact.',
    features: [
      'Live stock levels — updated on every sale & purchase',
      'Expiry tracking with proactive 15-day alerts',
      'Dead stock detection with clearance suggestions',
      'Smart reorder scoring — see which item needs restocking most urgently',
    ],
    highlights: [{ label: 'FEFO Tracking', color: 'bg-amber-100 text-amber-700' }, { label: 'Live Stock', color: 'bg-emerald-100 text-emerald-700' }],
    Mockup: InventoryMockup,
  },
  {
    id: 'purchases',
    badge: 'Purchases & Suppliers',
    badgeColor: 'bg-violet-100 text-violet-700',
    headline: 'AI invoice scanning + one-click reorders',
    desc: 'Photograph a supplier bill and let AI extract every line item in seconds. Maintain a supplier directory and send WhatsApp reorder messages to your distributor in one tap when stock runs low.',
    features: [
      'AI photo invoice parsing — snap, review, and confirm',
      'Supplier contact directory with full purchase history',
      'One-click WhatsApp reorder to your distributor',
      'Full purchase ledger with edit, soft-delete & restore',
    ],
    highlights: [{ label: 'AI Parsing', color: 'bg-violet-100 text-violet-700' }, { label: 'One-click Order', color: 'bg-green-100 text-green-700' }],
    Mockup: PurchaseMockup,
  },
  {
    id: 'reports',
    badge: 'Accounting & GST',
    badgeColor: 'bg-amber-100 text-amber-700',
    headline: 'GST-ready reports, one tap from your CA',
    desc: 'Auto-generated P&L, GSTR-1, Day Book, Journal Ledger, Purchase Register, and Balance Sheet. Share monthly reports directly to your CA on WhatsApp or email in a single tap.',
    features: [
      'GSTR-1 ready — auto-calculated from your recorded sales',
      'P&L, Day Book, Sales & Purchase Registers',
      'Expense tracking with category breakdown',
      'One-click share to CA via WhatsApp or email',
    ],
    highlights: [{ label: 'GST Ready', color: 'bg-amber-100 text-amber-700' }, { label: 'Share to CA', color: 'bg-blue-100 text-blue-700' }],
    Mockup: ReportsMockup,
  },
];

// ── Lead capture form ─────────────────────────────────────────────────────────

const leadSchema = z.object({
  name:      z.string().min(2, 'Enter your name'),
  whatsapp:  z.string().regex(/^[6-9]\d{9}$/, 'Enter a valid 10-digit WhatsApp number'),
  city:      z.string().min(2, 'Enter your city'),
  shop_name: z.string().optional(),
});

function ContactForm() {
  const [submitted, setSubmitted] = useState(false);
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(leadSchema),
  });

  const onSubmit = async (data) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      await axios.post(`${backendUrl}/api/leads`, data);
      setSubmitted(true);
    } catch {
      toast.error('Something went wrong. Please try WhatsApp or email below.');
    }
  };

  if (submitted) {
    return (
      <div className="bg-white rounded-2xl border border-emerald-200 shadow-sm p-10 text-center">
        <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="h-7 w-7 text-emerald-600" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">We'll be in touch!</h3>
        <p className="text-gray-500 text-sm">
          We'll WhatsApp you within a few hours to schedule your free demo.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 space-y-5">
      <div className="space-y-1.5">
        <Label htmlFor="lf-name">Your name <span className="text-red-500">*</span></Label>
        <Input id="lf-name" placeholder="Ramesh Gupta" {...register('name')} />
        {errors.name && <p className="text-xs text-red-500">{errors.name.message}</p>}
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="lf-wa">WhatsApp number <span className="text-red-500">*</span></Label>
        <Input id="lf-wa" placeholder="9876543210" maxLength={10} {...register('whatsapp')} />
        {errors.whatsapp && <p className="text-xs text-red-500">{errors.whatsapp.message}</p>}
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="lf-city">City <span className="text-red-500">*</span></Label>
        <Input id="lf-city" placeholder="Pune" {...register('city')} />
        {errors.city && <p className="text-xs text-red-500">{errors.city.message}</p>}
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="lf-shop">Shop name <span className="text-gray-400 font-normal">(optional)</span></Label>
        <Input id="lf-shop" placeholder="Gupta General Store" {...register('shop_name')} />
      </div>
      <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white gap-2" disabled={isSubmitting}>
        {isSubmitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : <>Get a free demo <ArrowRight className="h-4 w-4" /></>}
      </Button>
      <p className="text-center text-xs text-gray-400">No spam. We'll only reach out to set up your demo.</p>
    </form>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function Landing() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white text-gray-900 font-sans">

      {/* ── Nav ─────────────────────────────────────────────────────── */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-gray-100 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-emerald-600 rounded-lg flex items-center justify-center">
              <Store className="h-5 w-5 text-white" />
            </div>
            <span className="text-xl font-bold text-gray-900">KiranaFlow</span>
          </div>
          <div className="hidden md:flex items-center gap-8 text-sm text-gray-600">
            <a href="#features" className="hover:text-emerald-600 transition-colors">Features</a>
            <a href="#pricing" className="hover:text-emerald-600 transition-colors">Pricing</a>
            <a href="#faq" className="hover:text-emerald-600 transition-colors">FAQ</a>
            <button onClick={scrollToContact} className="hover:text-emerald-600 transition-colors">Contact</button>
          </div>
          <div className="hidden md:flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/login')}>Login</Button>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1" onClick={scrollToContact}>
              Get Started <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          </div>
          <button
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
        {menuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 px-4 py-3 space-y-1">
            <a href="#features" className="block py-2.5 text-sm text-gray-700 hover:text-emerald-600 font-medium" onClick={() => setMenuOpen(false)}>Features</a>
            <a href="#pricing"  className="block py-2.5 text-sm text-gray-700 hover:text-emerald-600 font-medium" onClick={() => setMenuOpen(false)}>Pricing</a>
            <a href="#faq"      className="block py-2.5 text-sm text-gray-700 hover:text-emerald-600 font-medium" onClick={() => setMenuOpen(false)}>FAQ</a>
            <button className="block py-2.5 text-sm text-gray-700 hover:text-emerald-600 font-medium w-full text-left" onClick={() => { scrollToContact(); setMenuOpen(false); }}>Contact</button>
            <div className="flex gap-3 pt-2 pb-1">
              <Button variant="outline" size="sm" className="flex-1" onClick={() => { navigate('/login'); setMenuOpen(false); }}>Login</Button>
              <Button size="sm" className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => { scrollToContact(); setMenuOpen(false); }}>Get Started</Button>
            </div>
          </div>
        )}
      </nav>

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-emerald-50 via-white to-amber-50 pt-16 pb-20 px-4">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-emerald-100/50 via-transparent to-transparent pointer-events-none" />
        <div className="max-w-5xl mx-auto text-center relative">
          <div className="flex items-center justify-center gap-2 mb-3">
            <svg viewBox="0 0 30 20" width="28" height="19" className="rounded-sm flex-shrink-0 shadow-sm">
              <rect width="30" height="6.67" fill="#FF9933"/>
              <rect y="6.67" width="30" height="6.67" fill="#FFFFFF"/>
              <rect y="13.33" width="30" height="6.67" fill="#138808"/>
              <circle cx="15" cy="10" r="2.8" fill="none" stroke="#000080" strokeWidth="0.7"/>
              <circle cx="15" cy="10" r="0.5" fill="#000080"/>
            </svg>
            <span className="text-sm font-semibold text-orange-500 tracking-wide">Proudly built in India</span>
          </div>
          <Badge className="mb-6 bg-emerald-100 text-emerald-700 border-0 px-4 py-1.5 text-sm font-medium">
            Complete ERP built for Indian supermarkets & kirana stores
          </Badge>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-gray-900 leading-tight tracking-tight mb-6">
            One app to run<br />
            <span className="text-emerald-600">your entire store.</span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-500 max-w-2xl mx-auto mb-8 leading-relaxed">
            Billing, inventory, purchases, accounting, and GST — all in one place.
            Send bills on WhatsApp, reports to your CA, and orders to your supplier
            in a single tap.
          </p>
          <div className="flex flex-wrap justify-center gap-2 mb-10">
            {['⚡ Fast POS Billing', '📲 WhatsApp Bills', '🤖 AI Invoice Scan', '📊 GST Reports', '📦 Smart Reorder', '🔌 Works Offline'].map(f => (
              <span key={f} className="bg-white border border-gray-200 text-gray-600 text-xs font-medium px-3 py-1.5 rounded-full shadow-sm">{f}</span>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white text-base px-8 gap-2 shadow-lg shadow-emerald-200" onClick={scrollToContact}>
              Get Started <ArrowRight className="h-4 w-4" />
            </Button>
            <Button size="lg" variant="outline" className="text-base px-8 gap-2" onClick={() => navigate('/login')}>
              <BarChart2 className="h-4 w-4" /> Login to Dashboard
            </Button>
          </div>
          <p className="text-sm text-gray-400 mt-4">Personal onboarding · Live in 24 hours · No long-term lock-in</p>
        </div>

        {/* Hero App Mockup */}
        <div className="max-w-4xl mx-auto mt-14 px-0 sm:px-4">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
            <div className="bg-gray-100 px-4 py-2.5 flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-400" />
              <div className="w-3 h-3 rounded-full bg-yellow-400" />
              <div className="w-3 h-3 rounded-full bg-green-400" />
              <div className="flex-1 mx-4 bg-white rounded px-3 py-1 text-xs text-gray-400 text-center">kiranaflow.app/pos</div>
            </div>
            <div className="flex bg-gray-50 min-h-[280px]">
              <div className="hidden sm:block w-40 bg-white border-r border-gray-100 p-3 space-y-1">
                <div className="flex items-center gap-1.5 mb-3 px-1">
                  <div className="w-5 h-5 bg-emerald-600 rounded flex items-center justify-center">
                    <Store className="h-3 w-3 text-white" />
                  </div>
                  <span className="text-xs font-bold text-emerald-600">KiranaFlow</span>
                </div>
                {[
                  { label: 'Dashboard',  active: false },
                  { label: 'POS Billing', active: true },
                  { label: 'Inventory',  active: false },
                  { label: 'Purchases',  active: false },
                  { label: 'Reports',    active: false },
                  { label: 'Customers',  active: false },
                ].map(({ label, active }) => (
                  <div key={label} className={`text-[10px] px-2.5 py-1.5 rounded-lg ${active ? 'bg-emerald-600 text-white font-semibold' : 'text-gray-500'}`}>
                    {label}
                  </div>
                ))}
              </div>
              <div className="flex-1 p-4 sm:p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-bold text-gray-700">POS — Customer 1</div>
                  <div className="flex gap-1">
                    {['C1', 'C2', 'C3'].map((c, i) => (
                      <div key={c} className={`text-[9px] px-2 py-1 rounded-md font-semibold ${i === 0 ? 'bg-emerald-600 text-white' : 'bg-gray-200 text-gray-500'}`}>{c}</div>
                    ))}
                  </div>
                </div>
                <div className="space-y-1.5">
                  {[['Tata Salt 1kg', '₹22', '2', '₹44'], ['Amul Butter 500g', '₹270', '1', '₹270'], ['Maggi 70g ×4', '₹74', '3', '₹222']].map(([name, price, qty, total]) => (
                    <div key={name} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-gray-100 text-[10px]">
                      <span className="text-gray-700 font-medium flex-1">{name}</span>
                      <span className="text-gray-400 w-10 text-right">{price}</span>
                      <span className="text-gray-400 w-6 text-center mx-1">×{qty}</span>
                      <span className="text-gray-800 font-bold w-10 text-right">{total}</span>
                    </div>
                  ))}
                </div>
                <div className="bg-emerald-600 rounded-xl p-3 flex items-center justify-between text-white">
                  <div>
                    <div className="text-[10px] text-emerald-200">Total</div>
                    <div className="text-lg font-extrabold">₹536</div>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <div className="bg-white text-emerald-700 text-[9px] font-bold px-3 py-1 rounded-lg flex items-center gap-1">
                      <MessageSquare className="h-2.5 w-2.5" /> Send Bill on WhatsApp
                    </div>
                    <div className="bg-emerald-500 text-white text-[9px] font-bold px-3 py-1 rounded-lg text-center">
                      Checkout →
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Stats ────────────────────────────────────────────────────── */}
      <section className="bg-emerald-700 py-12 px-4">
        <div className="max-w-4xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 text-white text-center">
          {STATS.map(({ icon: Icon, value, label }) => (
            <div key={label}>
              <Icon className="h-5 w-5 mx-auto mb-1.5 opacity-70" />
              <div className="text-3xl font-extrabold">{value}</div>
              <div className="text-emerald-200 text-xs mt-1">{label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Core Modules ─────────────────────────────────────────────── */}
      <section id="features" className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <Badge className="mb-4 bg-emerald-100 text-emerald-700 border-0">Complete ERP</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">Everything your store needs</h2>
            <p className="text-gray-500 mt-3 max-w-xl mx-auto">
              From the first barcode scan to the final monthly report — KiranaFlow handles it all.
            </p>
          </div>
          <div className="space-y-20">
            {MODULES.map(({ id, badge, badgeColor, headline, desc, features, highlights, Mockup }, idx) => (
              <div key={id} className={`flex flex-col ${idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} gap-10 md:gap-16 items-center`}>
                <div className="flex-1 space-y-5">
                  <Badge className={`${badgeColor} border-0 text-xs font-semibold px-3 py-1`}>{badge}</Badge>
                  <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 leading-tight">{headline}</h3>
                  <p className="text-gray-500 leading-relaxed">{desc}</p>
                  <ul className="space-y-2.5">
                    {features.map(f => (
                      <li key={f} className="flex items-start gap-2.5 text-sm text-gray-600">
                        <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <div className="flex gap-2 flex-wrap pt-1">
                    {highlights.map(h => (
                      <span key={h.label} className={`text-xs font-semibold px-3 py-1 rounded-full ${h.color}`}>{h.label}</span>
                    ))}
                  </div>
                </div>
                <div className="flex-1 w-full">
                  <Mockup />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── One-Click Actions ─────────────────────────────────────────── */}
      <section className="bg-gray-900 py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-emerald-900 text-emerald-300 border-0">One-Click Everything</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-white">Actions that save you hours every week</h2>
            <p className="text-gray-400 mt-3 max-w-xl mx-auto">
              The most frequent, most valuable actions in your store — done in a single tap.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {ONE_CLICK.map(({ icon: Icon, iconBg, title, desc }) => (
              <div key={title} className="bg-gray-800 rounded-2xl p-6 flex gap-4 items-start border border-gray-700 hover:border-gray-600 transition-colors">
                <div className={`${iconBg} w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg`}>
                  <Icon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1.5">{title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Additional Features ───────────────────────────────────────── */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-emerald-100 text-emerald-700 border-0">Built-in intelligence</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">And much more</h2>
            <p className="text-gray-500 mt-3 max-w-lg mx-auto">Deep capabilities built for Indian supermarket realities.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {EXTRA_FEATURES.map(({ icon: Icon, color, title, desc }) => (
              <div key={title} className="p-5 rounded-2xl border border-gray-100 hover:shadow-md transition-shadow bg-white">
                <div className={`w-10 h-10 rounded-xl ${color} flex items-center justify-center mb-3`}>
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-1.5 text-sm">{title}</h3>
                <p className="text-xs text-gray-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How It Works ─────────────────────────────────────────────── */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-14">
            <Badge className="mb-4 bg-emerald-100 text-emerald-700 border-0">Getting started</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">Live in 24 hours, guided by us</h2>
            <p className="text-gray-500 mt-3 max-w-lg mx-auto">We personally onboard every store. No self-serve complexity — we do the heavy lifting.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { step: '01', icon: Phone,        title: 'Contact us',       desc: 'Reach out on WhatsApp or email. We schedule a quick onboarding call.' },
              { step: '02', icon: Package,      title: 'Upload catalog',   desc: 'Add products via CSV, barcode scan, or manually. We guide you through.' },
              { step: '03', icon: ShoppingCart, title: 'Start billing',    desc: 'POS is live on day one — barcode scanner, multi-cart, offline ready.' },
              { step: '04', icon: TrendingUp,   title: 'Get smarter',      desc: 'Smart Actions, AI insights, and GST reports kick in as your data grows.' },
            ].map(({ step, icon: Icon, title, desc }) => (
              <div key={step} className="relative text-center">
                <div className="text-5xl font-black text-emerald-100 absolute -top-3 left-1/2 -translate-x-1/2 select-none leading-none">{step}</div>
                <div className="relative pt-5">
                  <div className="w-12 h-12 bg-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg shadow-emerald-200">
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-sm font-bold text-gray-900 mb-1.5">{title}</h3>
                  <p className="text-gray-500 text-xs leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing ──────────────────────────────────────────────────── */}
      <section id="pricing" className="py-20 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <Badge className="mb-4 bg-emerald-100 text-emerald-700 border-0">Simple pricing</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">Plans for every stage of growth</h2>
            <p className="text-gray-500 mt-3">Transparent pricing — lock in early to save.</p>
          </div>

          {/* Intro offer banner */}
          <div className="bg-gradient-to-r from-emerald-900 via-emerald-600 to-teal-500 rounded-2xl px-6 py-5 mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-white">
            <div>
              <span className="text-xs font-bold bg-white/20 border border-white/30 rounded-full px-3 py-0.5 uppercase tracking-wider mb-2 inline-block">Limited Time</span>
              <div className="text-xl font-extrabold">Introductory Launch Pricing — For Early Adopters</div>
              <div className="text-emerald-100 text-sm mt-1">Regular pricing shown for comparison. Lock in early pricing before it changes.</div>
            </div>
            <div className="text-right flex-shrink-0">
              <div className="text-4xl font-black leading-none">Save ₹5,000+</div>
              <div className="text-emerald-200 text-xs mt-1">on setup alone</div>
            </div>
          </div>

          {/* Tier cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch mb-8">
            {TIERS.map((tier) => {
              const Icon = tier.icon;
              return (
                <div key={tier.key}
                  className={`rounded-2xl border-2 overflow-hidden flex flex-col ${tier.color} ${tier.popular ? 'shadow-2xl shadow-emerald-100 md:scale-105' : 'shadow-sm'}`}>

                  {/* Header */}
                  <div className={`px-6 py-5 ${tier.headerBg}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Icon className={`h-5 w-5 ${tier.popular ? 'text-white' : 'text-gray-600'}`} />
                        <span className={`font-extrabold text-xl ${tier.headerText}`}>{tier.label}</span>
                      </div>
                      <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${tier.badgeBg}`}>{tier.badge}</span>
                    </div>
                    <p className={`text-sm ${tier.popular ? 'text-emerald-100' : 'text-gray-500'}`}>{tier.desc}</p>
                  </div>

                  {/* Pricing block */}
                  <div className="px-6 py-5 border-b border-gray-100 bg-white">
                    {tier.pricing ? (
                      <>
                        <div className="text-xs font-bold text-emerald-700 uppercase tracking-wider mb-3">Subscription — Software Access</div>
                        <div className="space-y-1.5">
                          {tier.pricing.map(({ label, regular, intro }) => (
                            <div key={label} className="flex items-center justify-between py-1.5 border-b border-emerald-50 last:border-b-0">
                              <span className="text-sm font-semibold text-gray-700">{label}</span>
                              <div className="flex items-center gap-3">
                                <span className="text-sm text-gray-400 line-through">{regular}</span>
                                <span className="text-lg font-extrabold text-emerald-700">{intro}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                        <p className="text-xs text-gray-500 mt-3 leading-relaxed">
                          Includes 3 desktop/laptop devices &nbsp;·&nbsp; Extra device: <del>₹2,000</del> <strong className="text-emerald-700">₹1,500</strong>/yr
                        </p>
                      </>
                    ) : (
                      <div className="text-center py-1">
                        <div className="text-xs font-bold uppercase tracking-wider mb-0.5" style={{ color: tier.trialLabelColor }}>Free Trial Included</div>
                        <div className="text-6xl font-black leading-none" style={{ color: tier.trialColor }}>3</div>
                        <div className="text-base font-bold mt-0.5" style={{ color: tier.trialLabelColor }}>months free</div>
                        <div className="text-xs text-gray-500 mt-2 leading-relaxed">{tier.trialNote}</div>
                      </div>
                    )}
                  </div>

                  {/* Features */}
                  <div className="px-6 py-5 bg-white flex-1 flex flex-col">
                    <div className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">What's included</div>
                    <ul className="space-y-2 flex-1">
                      {tier.features.map((f, i) => (
                        i === 0 && f.endsWith('plus:') ? (
                          <li key={f} className="flex items-center gap-2 text-sm text-gray-500 font-semibold mb-1">
                            <span className="inline-block w-6 h-px bg-gray-300 flex-shrink-0" />
                            {f}
                          </li>
                        ) : (
                          <li key={f} className="flex items-start gap-2 text-sm text-gray-600">
                            <CheckCircle2 className={`h-4 w-4 flex-shrink-0 mt-0.5 ${tier.dotColor}`} />
                            {f}
                          </li>
                        )
                      ))}
                    </ul>
                    <Button
                      className={`w-full mt-5 ${tier.popular ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}`}
                      variant={tier.popular ? 'default' : 'outline'}
                      onClick={scrollToContact}
                    >
                      {tier.pricing ? 'Get Started' : 'Start Free Trial'}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Setup + Devices row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Setup card */}
            <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm">
              <div className="flex items-start justify-between mb-1">
                <div className="font-bold text-gray-900 text-base">One-Time Setup &amp; Onboarding</div>
                <div className="text-right flex-shrink-0 ml-4">
                  <span className="text-sm text-gray-400 line-through block">₹25,000</span>
                  <span className="text-xl font-extrabold text-emerald-700">₹20,000</span>
                </div>
              </div>
              <p className="text-xs text-gray-500 mb-4">Introductory · One-time per store</p>
              <ul className="space-y-2">
                {[
                  'Map & understand your current ERP and workflows',
                  'Export, clean, restructure & normalize your data',
                  'Upload to KiranaFlow with end-to-end compatibility checks',
                  'Train all staff, managers & owners on the app',
                  'Set up thermal printer & barcode scanner',
                  'Full onboarding until your store is live',
                ].map(item => (
                  <li key={item} className="flex items-start gap-2 text-sm text-gray-600">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Devices + Mobile card */}
            <div className="flex flex-col gap-4">
              <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-6 shadow-sm flex-1">
                <div className="font-bold text-emerald-800 text-base mb-4">🖥️ Desktop &amp; Laptop Access</div>
                <div className="space-y-0">
                  <div className="flex items-center justify-between py-2.5 border-b border-emerald-100">
                    <span className="text-sm font-semibold text-gray-700">3 devices (included)</span>
                    <span className="text-xl font-extrabold text-emerald-700">Free</span>
                  </div>
                  <div className="flex items-center justify-between py-2.5">
                    <span className="text-sm font-semibold text-gray-700">Each extra device</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-400 line-through">₹2,000/yr</span>
                      <span className="text-xl font-extrabold text-emerald-700">₹1,500/yr</span>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-3">Works on any desktop or laptop — no installation needed, runs directly in your browser. Multiple staff can bill simultaneously.</p>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-violet-100 border border-purple-100 rounded-2xl p-5 shadow-sm text-center">
                <div className="text-base font-bold text-purple-700 mb-0.5">📱 Mobile App</div>
                <div className="text-xs text-purple-500 mb-3">iPhone &amp; Android</div>
                <span className="bg-purple-600 text-white text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-wide">Coming Soon</span>
                <p className="text-xs text-purple-400 mt-3 leading-relaxed">Native app with camera scanning &amp; offline billing — in development.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────────── */}
      <section id="faq" className="py-20 px-4 bg-white">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-emerald-100 text-emerald-700 border-0">FAQ</Badge>
            <h2 className="text-3xl font-bold text-gray-900">Frequently asked questions</h2>
          </div>
          <div className="space-y-3">
            {FAQS.map(({ q, a }, i) => (
              <div key={i} className="border border-gray-100 rounded-xl overflow-hidden bg-white shadow-sm">
                <button
                  className="w-full flex items-center justify-between px-5 py-4 text-left text-sm font-medium text-gray-900 hover:bg-gray-50 transition-colors"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span className="pr-3">{q}</span>
                  {openFaq === i
                    ? <ChevronUp className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    : <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />}
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-4 text-sm text-gray-500 leading-relaxed border-t border-gray-50 pt-3">{a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Contact / Team ───────────────────────────────────────────── */}
      <section id="contact" className="py-20 px-4 bg-gradient-to-br from-emerald-50 via-white to-blue-50">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-emerald-100 text-emerald-700 border-0">Get in touch</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Interested? Let's talk.</h2>
            <p className="text-gray-500 text-lg max-w-xl mx-auto">
              Tell us your name and WhatsApp number — we'll reach out and get your store live within 24 hours.
            </p>
          </div>

          {/* Two-column: trust + form */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center mb-16">
            {/* Left — trust signals */}
            <div className="space-y-6">
              {[
                { icon: CheckCircle2, color: 'text-emerald-600', title: 'Free demo, no pressure', desc: 'We walk you through KiranaFlow live on a call. No credit card, no commitment.' },
                { icon: MessageSquare, color: 'text-green-600', title: 'WhatsApp-first support', desc: 'We onboard you on WhatsApp and stay available for any questions after you go live.' },
                { icon: Zap, color: 'text-amber-500', title: 'Live in under 24 hours', desc: 'We personally set up your catalog and open stock — you start billing the same day.' },
              ].map(({ icon: Icon, color, title, desc }) => (
                <div key={title} className="flex gap-4">
                  <div className={`mt-0.5 flex-shrink-0 ${color}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 text-sm">{title}</div>
                    <div className="text-gray-500 text-sm mt-0.5 leading-relaxed">{desc}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Right — contact form */}
            <ContactForm />
          </div>

          {/* Team cards */}
          <div className="text-center mb-8">
            <p className="text-sm text-gray-400 font-medium uppercase tracking-wide">Or reach us directly</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10">
            {TEAM.map(({ name, role, phone, displayPhone, email, initials, color, shadow }) => (
              <div key={name} className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-md hover:border-emerald-200 transition-all text-center">
                <div className={`w-16 h-16 ${color} rounded-2xl flex items-center justify-center mx-auto mb-4 text-white text-xl font-bold shadow-lg ${shadow}`}>
                  {initials}
                </div>
                <div className="font-bold text-gray-900 mb-0.5 text-base">{name}</div>
                <div className="text-xs text-gray-400 mb-5">{role}</div>
                <div className="space-y-2.5">
                  <a
                    href={`https://wa.me/91${phone}?text=${WA_MSG}`}
                    target="_blank" rel="noreferrer"
                    className="flex items-center justify-center gap-2 bg-green-50 hover:bg-green-100 text-green-700 text-xs font-semibold px-3 py-2.5 rounded-xl transition-colors"
                  >
                    <MessageSquare className="h-3.5 w-3.5 flex-shrink-0" />
                    {displayPhone}
                  </a>
                  <a
                    href={`mailto:${email}`}
                    className="flex items-center justify-center gap-2 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-semibold px-3 py-2.5 rounded-xl transition-colors break-all"
                  >
                    <Mail className="h-3.5 w-3.5 flex-shrink-0" />
                    {email}
                  </a>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center">
            <Button size="lg" variant="outline" className="gap-2 px-8" onClick={() => navigate('/login')}>
              <BarChart2 className="h-4 w-4" /> Existing user? Login to dashboard
            </Button>
          </div>
        </div>
      </section>

      {/* ── Footer ───────────────────────────────────────────────────── */}
      <footer className="bg-gray-900 text-gray-400 py-12 px-4">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-emerald-500 rounded-lg flex items-center justify-center">
              <Store className="h-4 w-4 text-white" />
            </div>
            <span className="text-white font-bold">KiranaFlow</span>
            <span className="text-xs ml-2">Complete ERP for Indian Stores</span>
          </div>
          <div className="flex gap-6 text-sm flex-wrap justify-center">
            <a href="#features" className="hover:text-white transition-colors">Features</a>
            <a href="#pricing"  className="hover:text-white transition-colors">Pricing</a>
            <a href="#faq"      className="hover:text-white transition-colors">FAQ</a>
            <button onClick={scrollToContact} className="hover:text-white transition-colors">Contact</button>
            <button onClick={() => navigate('/login')} className="hover:text-white transition-colors">Login</button>
          </div>
          <div className="flex gap-5 text-xs flex-wrap justify-center text-gray-500">
            <button onClick={() => navigate('/terms')}   className="hover:text-gray-300 transition-colors">Terms</button>
            <button onClick={() => navigate('/privacy')} className="hover:text-gray-300 transition-colors">Privacy</button>
            <button onClick={() => navigate('/refund')}  className="hover:text-gray-300 transition-colors">Refunds</button>
          </div>
          <div className="text-xs text-center md:text-right space-y-1">
            <div className="text-gray-500">rohankakad712@gmail.com</div>
            <div className="flex items-center gap-1.5 justify-center md:justify-end">
              <span className="text-base leading-none">🇮🇳</span>
              <span className="text-orange-400 font-semibold">Building for every store in Bharat</span>
            </div>
            <div className="text-gray-600">© 2026 KiranaFlow · Proudly built in India</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
